import React, { useMemo } from 'react';
import classNames from 'classnames';
import { fetchFilteredParkingReservations } from 'Services/Api/_HumanWritten/ParkingService';
import { STAFF_TABLE_SECTIONS, useResetFiltersOnSectionEntry } from 'Util/_HumanWritten/Navigation/staffTableSections';
import StaffParkingReservationFilters from './StaffParkingReservationFilters';
import { useStaffParkingReservationsStore } from './StaffParkingReservationsFilterStore';
import { StaffParkingReservationsCollection } from './StaffParkingReservationsCollection';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useDebounce } from 'Hooks/useDebounce';

const MINIMUM_LOADER_TIME = 300;

interface StaffParkingReservationsListOuterProps {
	userId?: string;
	onResultsPage?: boolean;
	hideCustomerColumn?: boolean;
}

export function StaffParkingReservationsListOuter({
	userId,
	onResultsPage,
	hideCustomerColumn,
}: StaffParkingReservationsListOuterProps = {}) {
	const perPage = 10;

	// Above the store subscription on purpose — see useResetFiltersOnSectionEntry.
	useResetFiltersOnSectionEntry(STAFF_TABLE_SECTIONS.PARKING_RESERVATIONS, () =>
		useStaffParkingReservationsStore.getState().resetFilters(),
	);

	const { filters, updateFilters, selectedReservationIds, setSelectedReservationIds } =
		useStaffParkingReservationsStore();

	const debouncedSearchFilters = useDebounce(
		{
			...filters,
			pageNo: undefined,
		},
		150,
	);

	const queryFilters = useMemo(
		() => ({
			...debouncedSearchFilters,
			pageNo: filters.pageNo,
		}),
		[debouncedSearchFilters, filters.pageNo],
	);

	const { data, isLoading, isFetching, refetch, dataUpdatedAt } = useQuery({
		queryKey: ['staffParkingReservations', userId, queryFilters, perPage],
		queryFn: async () => {
			const apiPromise = fetchFilteredParkingReservations({
				searchString: queryFilters.searchString,
				userId,
				startDate: queryFilters.startDate,
				endDate: queryFilters.endDate,
				dateFilterType: queryFilters.dateFilterType,
				statusFilters: queryFilters.statusFilters,
				locationFilters: queryFilters.locationFilters,
				orderBy: queryFilters.orderBy,
				orderByDescending: queryFilters.orderByDescending,
				skip: perPage * (queryFilters.pageNo || 0),
				take: perPage,
			});

			const [result] = await Promise.all([
				apiPromise,
				new Promise(resolve => setTimeout(resolve, MINIMUM_LOADER_TIME)),
			]);

			return result;
		},
		placeholderData: keepPreviousData,
		staleTime: 1000 * 60,
	});

	const handleRefresh = () => {
		refetch();
	};

	return (
		<div className={classNames(!onResultsPage && 'frontend-admin-page', 'staff-parking-reservations')}>
			{!onResultsPage && (
				<>
					<div className="frontend-admin-page__layout--wide">
						<h1>Parking Reservations</h1>
					</div>
					<div className="page-header__border" />
				</>
			)}

			<StaffParkingReservationFilters
				setRefresh={handleRefresh}
				totalReservationCount={data?.count ?? 0}
				hideCustomerColumn={hideCustomerColumn || onResultsPage}
			/>

			<StaffParkingReservationsCollection
				reservations={data?.data ?? null}
				totalReservationCount={data?.count ?? 0}
				perPage={perPage}
				filters={filters}
				setFilters={updateFilters}
				isLoading={isLoading || isFetching}
				lastUpdated={dataUpdatedAt ? new Date(dataUpdatedAt) : new Date()}
				refresh={handleRefresh}
				selectedReservationIds={selectedReservationIds}
				setSelectedReservationIds={setSelectedReservationIds}
				hideCustomerColumn={hideCustomerColumn || onResultsPage}
			/>
		</div>
	);
}
